import React, {useState, useEffect, useRef, useMemo} from 'react';
import { useFormContext } from '../../Provider';
import useValidators from '../../Validators/useValidator';
import CommonFieldWrapper from '../CommonFieldWrapper';
import Loading, { PageLoading } from '../../../Loading';
import { preload } from '../../../../Services/helpers';
import Modal from '../../../Modal';
import Form, { Actions, Assets } from '../..';
import { getAssets } from '../../../../Data/Assets';
import { useDocuments } from '../../../../Data/Documents';
import { useSession } from '../../../Session';
import API from '../../../../Services/API';
import Icon from '../../../Icons/Icon';
import { tagLengths } from '../../../../Data/Templates';
import { useDialogues } from '../../../Dialogues';
import { Editor } from '@tinymce/tinymce-react';

const fallbackValue = '<p>Enter your text here</p>';

let imageResolver = null;

const TheWysiwyg = ({label, name, help, onChange, initialValue, disabled, validators, features, mode = 'html', bottomMargin = 50, imageTags, previousTags, smsLengthWarning, aiPromptOptions}) => {
    const { formData, updateField, updateValidity, isValidating, isSubmitting, formDisabled } = useFormContext();
    const value = formatInput(
        formData[name] ?? initialValue ?? fallbackValue,
        mode
    );
    const {isValid, error} = useValidators(value, validators, name, updateValidity);
    const editorRef = useRef(null);
    const [isLoading, setIsLoading] = useState(true);
    const [pickingImage, setPickingImage] = useState(false);
    const {documents} = useDocuments();
    const {hasFeature} = useSession();

    const handleChange = (val) => {
        updateField(name, formatOutput(val, mode));
        onChange && onChange(formatOutput(val, mode));
    }

    const openImageModal = () => {
        setPickingImage(true);
        return new Promise((resolve, reject) => {
            imageResolver = resolve;
        });
    }

    const isDisabled = formDisabled || disabled || isSubmitting;

    useEffect(() => {
        if ((editorRef?.current ?? false) === false) {
            return;
        }

        editorRef.current.contentDocument.body.style.backgroundColor = isDisabled ? '#f3f4f6' : '#FFF';
        editorRef.current.contentDocument.body.style.color = isDisabled ? '#9ca3af' : '#000';

    }, [isDisabled]);

    const opts = useMemo(() => getOpts(features, imageTags, aiPromptOptions), [features, imageTags, aiPromptOptions]);

    // We need to wait for documents to load because we can't change the merge tags after the editor has initialised
    if (hasFeature('DOCUMENTS') && documents === null) {
        return <CommonFieldWrapper {...{label, name, help, value, isValid, isValidating, error, validators, isDisabled, padding: 'px-1 py-1.5'}}>
            <PageLoading short />
        </CommonFieldWrapper>;
    }

    return <CommonFieldWrapper {...{label, name, help, value, isValid, isValidating, error, validators, isDisabled, padding: 'px-1 py-1.5'}}>

        <Editor
            tinymceScriptSrc="/tinymce/tinymce.min.js"
            onInit={(evt, editor) => {
                setIsLoading(false);
                editorRef.current = editor;
            }}
            disabled={isDisabled}
            value={value}
            onEditorChange={(newValue, editor) => handleChange(newValue)}
            init={{
                ...opts,
                autoresize_bottom_margin: parseInt(bottomMargin),
                menubar: false,
                inline_boundaries: false,
                plugins: opts.plugins,
                toolbar: opts.toolbar,
                content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px; min-height: 25px !important; }',
                statusbar: false,
                branding: false,
                highlight_on_focus: false,
                mergetags_prefix: '{{',
                mergetags_suffix: '}}',
                mergetags_list: getMergeTags(documents),
                min_height: 25,
                height: 25,
                setup: (editor) => {
                    editor.ui.registry.addButton('soapyImage', {
                        text: null,
                        icon: 'image',
                        onAction: function () {
                            openImageModal().then(imageDetails => {
                                if (!imageDetails) {
                                    return;
                                }

                                editor.insertContent(`<img src="${imageDetails.url}" width="200" />`);
                            });
                        }
                    });
                }
            }}
        />

       {isLoading && <Loading size="2x" className="mb-10" center />}

       <ImagePicker {...{pickingImage, setPickingImage, imageTags, previousTags}} />

       {/* {emojiWarning && <EmojiWarning value={value} emojiWarning={emojiWarning} />} */}
       {smsLengthWarning && <SMSLengthWarning value={value} />}

    </CommonFieldWrapper>
}

const SMSLengthWarning = ({value}) => {
    const {info} = useDialogues();

    const showInfo = () => {
        info({
            title: 'Estimated SMS Credits',
            message: <>
                The number of credits is estimated based on the length of your message, using average lengths for template tags. The actual cost might differ.
                <br /><br />
                SMS messages have a maximum length of 160 characters. If the message exceeds this, it will be sent as multiple pieces, and each piece will use an SMS credit.<br />
                It will still appear to your customer as a single message.
                <br /><br />
                If you use special characters or emojis, the maximum length reduces to 70 characters per message. This might cause your message to be split into more pieces than expected.
                <br /><br />
                To avoid unnecessary costs we try to replace special characters when an SMS-safe alternative is available, but you should still avoid using them if possible.
            </>
        });
    }

    const [estimate, isGMSSafe] = useMemo(() => {
        if (!value) {
            return [null, null];
        }

        const regex = /(\{\{|%7B%7B)\s*(?<firstPart>[a-zA-Z]+)(?<middle>(\s|\.)*)(?<secondPart>[a-zA-Z\s]*)\s*(\}\}|%7D%7D)/g;
        const matches = value.match(regex) ?? [];

        const element = document.createElement('div');
        element.innerHTML = value;
        const cleaned = element.innerText.replace(/\s/g, ' ').trim();

        const tagLength = matches.reduce((acc, match) => acc + (tagLengths[match.replace(/[{}]/g, '')] ?? 11), 0);

        const withoutTags = matches.reduce((acc, tag) => {
            return acc.replace(tag, '');
        }, cleaned);

        const nonTagLength = withoutTags.length;

        const regexp = new RegExp("^[A-Za-z0-9 \\r\\n@£$¥èéùìòÇØøÅå\u0394_\u03A6\u0393\u039B\u03A9\u03A0\u03A8\u03A3\u0398\u039EÆæßÉ!\"#$%&'()*+,\\-./:;<=>?¡ÄÖÑÜ§¿äöñüà^{}\\\\\\[~\\]|\u20AC]*$");
        const isGMSSafe = regexp.test(cleaned);
        const partLength = isGMSSafe ? 160 : 70;

        const low = Math.ceil(((tagLength * 0.8) + nonTagLength) / partLength);
        const high = Math.ceil(((tagLength * 1.2) + nonTagLength) / partLength);

        const estimate = low === high ? <>{low}</> : <>{low} - {high}</>

        return [estimate, isGMSSafe];
    }, [value]);

    if (!value) {
        return null;
    }

    return <p className="text-xs text-muted m-2 cursor-pointer" onClick={() => showInfo()}>
        Estimated SMS credits per message: {estimate}. &nbsp; <u>Learn more</u>.
        {!isGMSSafe && <><br />
            <span className="mt-2 flex flex-row items-center text-orange-600 fill-orange-600">
                <Icon icon="TriangleExclamation" theme="solid" className="mr-2" />
                Special characters or emoji can use more credits and may be removed.
            </span>
        </>}
    </p>;
}

const ImagePicker = ({pickingImage, setPickingImage, imageTags, previousTags}) => {
    const handleSubmit = async (data) => {
        const assets = await getAssets([data.image]);

        imageResolver({
            url: assets[0].original,
        });
    }

    return <Modal isOpen={pickingImage} setIsOpen={setPickingImage} title="Pick an image" dismissible>
        <Form onSubmit={handleSubmit} closeModalOnSubmit>
            <Assets
                label="Image"
                name="image"
                tags={imageTags}
                previousTags={previousTags}
                single
                showPrevious
            />
            <Actions submit={{label: 'Insert'}} cancel />
        </Form>
    </Modal>
}

const getOpts = (features, imageTags, aiPromptOptions) => {
    const plugins = ['autoresize'];
    const toolbar = [];
    const extraOpts = {};

    preload('/tinymce/tinymce.min.js');
    preload('/tinymce/models/dom/model.min.js');
    preload('/tinymce/icons/default/icons.min.js');
    preload('/tinymce/themes/silver/theme.min.js');

    preload('/tinymce/skins/ui/oxide/skin.min.css');
    preload('/tinymce/skins/ui/oxide/content.min.css');
    preload('/tinymce/skins/content/default/content.min.css');

    // preload('/tinymce/plugins/autolink/plugin.min.js');
    preload('/tinymce/plugins/autoresize/plugin.min.js');

    if (features?.includes('mergetags')) {
        preload('/tinymce/plugins/mergetags/plugin.min.js');
        plugins.push('mergetags');
        toolbar.push('mergetags');
    }

    if (features?.includes('ai') && aiPromptOptions) {
        preload('/tinymce/plugins/ai/plugin.min.js');
        plugins.push('ai');
        toolbar.push('aidialog aishortcuts | ');
        extraOpts.ai_request = aiRequest;
        extraOpts.ai_shortcuts = aiShortcuts.filter(s => aiPromptOptions.includes(s.prompt));
    }

    if (features?.includes('images')) {
        if (!imageTags) {
            console.warn('You must provide imageTags if you want to use the image picker');
        } else {
            preload('/tinymce/plugins/image/plugin.min.js');
            plugins.push('image');
            toolbar.push('soapyImage');
        }
    }
    if (features?.includes('basicFormatting')) {
        plugins.push('link');
        plugins.push('emoticons');
        toolbar.push('bold italic emoticons link blocks fontsize');
        preload('/tinymce/plugins/link/plugin.min.js');
        preload('/tinymce/plugins/emoticons/plugin.min.js');
        preload('/tinymce/plugins/emoticons/js/emojis.min.js');
    }
    if (features?.includes('moreFormatting')) {
        toolbar.push('alignleft aligncenter alignright | removeformat');
    }
    if (features?.includes('advancedFormatting')) {
        plugins.push('lists');
        toolbar.push('bullist numlist outdent indent');
    }

    return {
        plugins: plugins,
        toolbar: toolbar.join(' '),
        ...extraOpts
    }
}

const aiRequest = (request, respondWith) => {
    return respondWith.string(async () => {
        const response = await API.post('/assistant/wysiwyg', request);
        return response.content;
    });
}

const aiPromptOptions = {
    FORMALISE_WEBSITE_CONTENT: 'formalise-website-content'
};

const aiShortcuts = [
    {
        title: 'Touch up grammar',
        prompt: 'Correct this grammar and spelling of this text (British English) but don\'t change it.',
        selection: true
    },
    {
        title: 'Make it more formal',
        prompt: 'formalise-website-content',
        selection: true
    },
    /*
    {
        title: 'Classical',
        subprompts:[
            { title: 'Dialogue', prompt: 'Convert this to a Socratic dialogue.', selection: true },
            { title: 'Homeric', prompt: 'Convert this to a Classical Epic.', selection: true }
        ]
    },
    */
];

const formatInput = (val, mode) => {
    if (mode === 'html') {
        return val;
    } else if (mode === 'singleLine' || mode === 'multiLine') {
        return '<p>'+val.replace(/(\r\n|\n|\r)/gmi, "</p>\n<p>")+'</p>';
    }
}

const formatOutput = (val, mode) => {
    if (mode === 'html') {
        return val;
    } else if (mode === 'singleLine') {
        return val.replace(/(<([^>]+)>)/gmi, "").replace(/(\r\n|\n|\r)+/gmi, " ").trim();
    } else if (mode === 'multiLine') {
        return val.replace(/(<([^>]+)>)/gmi, "\n").replace(/(\r\n|\n|\r)+/gmi, "\n").trim();
    }
}

const sortMergeTags = (a, b) => {
    if (a.title < b.title) {
        return -1;
    }
    if (a.title > b.title) {
        return 1;
    }

    return 0;
}

const getMergeTags = (documents) => {
    const tags = [
        {
            title: 'Business',
            menu: [
                { value: 'business.facebook',           title: 'Business Facebook' },
                { value: 'business.instagram',          title: 'Business Instagram' },
                { value: 'business.name',               title: 'Business Name' },
                { value: 'business.tagline',            title: 'Business Tagline' },
                { value: 'business.phoneNumber',        title: 'Business PhoneNumber' },
                { value: 'business.taxId',              title: 'Business Tax ID' },
                { value: 'business.trustPilot',         title: 'Business TrustPilot' },
                { value: 'business.twitter',            title: 'Business Twitter' },
                { value: 'business.website',            title: 'Business Website' },
                { value: 'business.whatsApp',           title: 'Business WhatsApp' },
                { value: 'business.mastodon',           title: 'Business Mastodon' },
                { value: 'business.threads',            title: 'Business Threads' },
                { value: 'business.youtube',            title: 'Business YouTube' },
                { value: 'business.google',             title: 'Business Google' },
                { value: 'business.bluesky',            title: 'Business Bluesky' },
            ].sort(sortMergeTags)
        },
        {
            title: 'Customer',
            menu: [
                { value: 'customer.balance',            title: 'Customer Balance' },
                { value: 'customer.balanceStatus',      title: 'Customer Balance Status' },
                { value: 'customer.email',              title: 'Customer Email' },
                { value: 'customer.firstName',          title: 'Customer First Name' },
                { value: 'customer.lastName',           title: 'Customer Last Name' },
                { value: 'customer.name',               title: 'Customer Full Name' },
                { value: 'customer.preferredName',      title: 'Customer Preferred Name' },
                { value: 'customer.businessName',       title: 'Customer Business Name' },
                { value: 'customer.autoName',           title: 'Customer Auto Name' },
                { value: 'customer.type',               title: 'Customer Type' },
                { value: 'customer.phoneNumber',        title: 'Customer Phone Number' },
                { value: 'customer.address',            title: 'Customer Address (Full)' },
                { value: 'customer.addressFirstLine',   title: 'Customer Address (First Line)' },
                { value: 'customer.addressTown',        title: 'Customer Address (Town)' },
                { value: 'customer.addressPostcode',    title: 'Customer Address (Postcode)' },
                { value: 'customer.addressCountry',     title: 'Customer Address (Country)' },
                { value: 'customer.addressStreetName',  title: 'Customer Address (Street Name)' },
                { value: 'customer.postcode',           title: 'Customer Postcode' },
                // { value: 'customer.shortId',            title: 'Customer Short ID' },
                { value: 'customer.code'   ,            title: 'Customer Code' },
                { value: 'customer.status',             title: 'Customer Status' },
                { value: 'customer.pin',                title: 'Customer Pin' },
            ].sort(sortMergeTags)
        },
        {
            title: 'Invoice',
            menu: [
                { value: 'invoice.total',               title: 'Invoice Total' },
                { value: 'invoice.outstanding',         title: 'Invoice Outstanding' },
                { value: 'invoice.paid',                title: 'Invoice Paid' },
                { value: 'invoice.status',              title: 'Invoice Status' },
                { value: 'invoice.dueDate',             title: 'Invoice Due Date' },
                { value: 'invoice.number',              title: 'Invoice Number' },
                // { value: 'invoice.shortId',             title: 'Short ID' },
                { value: 'invoice.code',                title: 'Invoice Code' },
            ].sort(sortMergeTags)
        },
        {
            title: 'Job',
            menu: [
                { value: 'job.cost',                    title: 'Job Cost' },
                { value: 'job.dueWeek',                 title: 'Job Due Week' },
                { value: 'job.frequency',               title: 'Job Frequency' },
                { value: 'job.lastDone',                title: 'Job LastDone' },
                { value: 'job.notes',                   title: 'Job Notes' },
                { value: 'job.purchaseOrder',           title: 'Job Purchase Order' },
                { value: 'job.scheduled',               title: 'Job Scheduled' },
                { value: 'job.scheduledDate',           title: 'Job Scheduled Date' },
                { value: 'job.estimatedTime',           title: 'Job Estimated Time' },
                { value: 'job.services',                title: 'Job Services' },
                // { value: 'job.shortId',                 title: 'Job Short ID' },
                { value: 'job.code',                    title: 'Job Code' },
                { value: 'job.status',                  title: 'Job Status' },
            ].sort(sortMergeTags)
        },
        {
            title: 'Quote',
            menu: [
                { value: 'quote.name',                  title: 'Quote Full Name' },
                { value: 'quote.firstName',             title: 'Quote First Name' },
                { value: 'quote.lastName',              title: 'Quote Last Name' },
                { value: 'quote.status',                title: 'Quote Status' },
                { value: 'quote.address',               title: 'Quote Address (Full)' },
                { value: 'quote.addressFirstLine',      title: 'Quote Address (First Line)' },
                { value: 'quote.addressTown',           title: 'Quote Address (Town)' },
                { value: 'quote.addressPostcode',       title: 'Quote Address (Postcode)' },
                { value: 'quote.addressCountry',        title: 'Quote Address (Country)' },
                { value: 'quote.addressStreetName',     title: 'Quote Address (Street Name)' },
            ].sort(sortMergeTags)
        },
        {
            title: 'User',
            menu: [
                { value: 'user.email',                  title: 'User Email' },
                { value: 'user.name',                   title: 'User Full Name' },
                { value: 'user.firstName',              title: 'User First Name' },
                { value: 'user.lastName',               title: 'User Last Name' },
            ].sort(sortMergeTags)
        },
        {
            title: 'Link',
            menu: [
                { value: 'link',                        title: 'Link' },
                { value: 'link.short',                  title: 'Short link' },
            ].sort(sortMergeTags)
        },
        {
            title: 'Messages',
            menu: [
                { value: 'message.full',                        title: 'Full' },
                { value: 'message.clipped',                  title: 'Clipped (fit within 1 SMS)' },
            ].sort(sortMergeTags)
        },
    ];

    if (documents && documents.length > 0) {
        tags.push({
            title: 'Document',
            menu: [
                // { value: 'document.linked',               title: 'Linked documents' },
                ...documents.map(d => ({
                    value: `document.${d.key}`,
                    title: d.title,
                }))
            ].sort(sortMergeTags)
        });
    }

    return tags.sort(sortMergeTags);
}

export default TheWysiwyg;

export {
    aiPromptOptions,
}
